import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    addOrgMember,
    assignChip,
    correctChip,
    createChip,
    createOrgMachine,
    createSettlementParty,
    createVendingOrg,
    deleteSettlementParty,
    fetchChip,
    fetchChipHistory,
    fetchChipMachines,
    fetchChipTransactions,
    fetchChipYearly,
    fetchCustomerChips,
    fetchCustomerVendingMemberships,
    fetchMyMachines,
    fetchMyTransactions,
    fetchMyYearly,
    fetchOrgChips,
    fetchOrgMachines,
    fetchOrgMembers,
    fetchOrgPersons,
    fetchOrgOverview,
    fetchOrgTransactions,
    fetchOrgYearly,
    fetchProductBilling,
    fetchSettlementConfig,
    fetchSettlementParties,
    fetchUnmappedProducts,
    fetchVendingOrgs,
    importOrgChips,
    importOrgTransactions,
    linkChip,
    removeOrgMember,
    requestChipTypeChange,
    resendChipRegistration,
    setChipReminder,
    topUpChip,
    updateChip,
    updateMachineSettlement,
    updateOrgMember,
    updateProductBilling,
    updateSettlementConfig,
    updateSettlementParty,
} from 'src/api/mitarbeiterchip';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import {
    ADD_ORG_MEMBER,
    ASSIGN_CHIP,
    CORRECT_CHIP,
    CREATE_CHIP,
    CREATE_ORG_MACHINE,
    CREATE_SETTLEMENT_PARTY,
    CREATE_VENDING_ORG,
    DELETE_SETTLEMENT_PARTY,
    fetchChip as fetchChipAction,
    fetchMyChips as fetchMyChipsAction,
    fetchOrgChips as fetchOrgChipsAction,
    fetchOrgMachines as fetchOrgMachinesAction,
    fetchOrgMembers as fetchOrgMembersAction,
    fetchOrgOverview as fetchOrgOverviewAction,
    fetchOrgTransactions as fetchOrgTransactionsAction,
    fetchProductBilling as fetchProductBillingAction,
    fetchSettlementConfig as fetchSettlementConfigAction,
    fetchSettlementParties as fetchSettlementPartiesAction,
    fetchUnmappedProducts as fetchUnmappedProductsAction,
    fetchVendingOrgs as fetchVendingOrgsAction,
    FETCH_CHIP,
    FETCH_CHIP_HISTORY,
    FETCH_CHIP_MACHINES,
    FETCH_CHIP_TRANSACTIONS,
    FETCH_CHIP_YEARLY,
    FETCH_CUSTOMER_MEMBERSHIPS,
    FETCH_MY_CHIPS,
    FETCH_MY_MACHINES,
    FETCH_MY_TRANSACTIONS,
    FETCH_MY_YEARLY,
    FETCH_ORG_CHIPS,
    FETCH_ORG_MACHINES,
    FETCH_ORG_MEMBERS,
    FETCH_ORG_PERSONS,
    FETCH_ORG_OVERVIEW,
    FETCH_ORG_TRANSACTIONS,
    FETCH_ORG_YEARLY,
    FETCH_PRODUCT_BILLING,
    FETCH_SETTLEMENT_CONFIG,
    FETCH_SETTLEMENT_PARTIES,
    FETCH_UNMAPPED_PRODUCTS,
    FETCH_VENDING_ORGS,
    IMPORT_ORG_CHIPS,
    IMPORT_ORG_TRANSACTIONS,
    LINK_CHIP,
    REMOVE_ORG_MEMBER,
    REPLACE_PRODUCT_BILLING,
    requestChip,
    requestChipFailure,
    requestChipHistory,
    requestChipHistoryFailure,
    requestChipHistorySuccess,
    requestChipMachines,
    requestChipMachinesFailure,
    requestChipMachinesSuccess,
    requestChipMutation,
    requestChipMutationFailure,
    requestChipMutationSuccess,
    requestChipSuccess,
    requestChipTransactions,
    requestChipTransactionsFailure,
    requestChipTransactionsSuccess,
    requestChipYearly,
    requestChipYearlyFailure,
    requestChipYearlySuccess,
    requestCustomerMemberships,
    requestCustomerMembershipsFailure,
    requestCustomerMembershipsSuccess,
    requestMyChips,
    requestMyChipsFailure,
    requestMyChipsSuccess,
    requestMyMachines,
    requestMyMachinesFailure,
    requestMyMachinesSuccess,
    requestMyTransactions,
    requestMyTransactionsFailure,
    requestMyTransactionsSuccess,
    requestMyYearly,
    requestMyYearlyFailure,
    requestMyYearlySuccess,
    requestOrgChips,
    requestOrgChipsFailure,
    requestOrgChipsSuccess,
    requestOrgMachines,
    requestOrgMachinesFailure,
    requestOrgMachinesSuccess,
    requestOrgPersons,
    requestOrgPersonsFailure,
    requestOrgPersonsSuccess,
    requestOrgMembers,
    requestOrgMembersFailure,
    requestOrgMembersSuccess,
    requestOrgOverview,
    requestOrgOverviewFailure,
    requestOrgOverviewSuccess,
    requestOrgTransactions,
    requestOrgTransactionsFailure,
    requestOrgTransactionsSuccess,
    requestOrgYearly,
    requestOrgYearlyFailure,
    requestOrgYearlySuccess,
    requestProductBilling,
    requestProductBillingFailure,
    requestProductBillingSuccess,
    requestSettlementConfig,
    requestSettlementConfigFailure,
    requestSettlementConfigSuccess,
    requestSettlementParties,
    requestSettlementPartiesFailure,
    requestSettlementPartiesSuccess,
    requestUnmappedProducts,
    requestUnmappedProductsFailure,
    requestUnmappedProductsSuccess,
    requestVendingOrgs,
    requestVendingOrgsFailure,
    requestVendingOrgsSuccess,
    REQUEST_CHIP_TYPE_CHANGE,
    RESEND_CHIP_REGISTRATION,
    SET_CHIP_REMINDER,
    TOPUP_CHIP,
    UPDATE_CHIP,
    UPDATE_MACHINE_SETTLEMENT,
    UPDATE_ORG_MEMBER,
    UPDATE_SETTLEMENT_CONFIG,
    UPDATE_SETTLEMENT_PARTY,
} from './actions';

/* Vending Orgs */
function* fetchVendingOrgsSaga(): SagaIterator {
    yield put(requestVendingOrgs());

    const { error, response } = yield call(fetchVendingOrgs);

    if (error) yield put(requestVendingOrgsFailure(error));
    else yield put(requestVendingOrgsSuccess(response));
}

export function* generateFetchVendingOrgsWatcher() {
    yield takeLatest(FETCH_VENDING_ORGS, fetchVendingOrgsSaga);
}

function* createVendingOrgSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(createVendingOrg, action.data);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Die Organisation konnte nicht angelegt werden! ' + (error?.message ?? ''),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestChipMutationFailure(error));
    } else {
        yield put(requestChipMutationSuccess(response));
        yield put(fetchVendingOrgsAction());
    }
}

export function* generateCreateVendingOrgWatcher() {
    yield takeLatest(CREATE_VENDING_ORG, createVendingOrgSaga);
}

/* My Chips */
function* fetchMyChipsSaga(action: AnyAction): SagaIterator {
    yield put(requestMyChips());

    const { error, response } = yield call(fetchCustomerChips, action.customerId);

    if (error) yield put(requestMyChipsFailure(error));
    else yield put(requestMyChipsSuccess(response));
}

export function* generateFetchMyChipsWatcher() {
    yield takeLatest(FETCH_MY_CHIPS, fetchMyChipsSaga);
}

/* Customer Vending Memberships */
function* fetchCustomerMembershipsSaga(action: AnyAction): SagaIterator {
    yield put(requestCustomerMemberships());

    const { error, response } = yield call(fetchCustomerVendingMemberships, action.customerId);

    if (error) yield put(requestCustomerMembershipsFailure(error));
    else yield put(requestCustomerMembershipsSuccess(response));
}

export function* generateFetchCustomerMembershipsWatcher() {
    yield takeLatest(FETCH_CUSTOMER_MEMBERSHIPS, fetchCustomerMembershipsSaga);
}

/* Org Chips */
function* fetchOrgChipsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrgChips());

    const { error, response } = yield call(fetchOrgChips, action.orgId, action.params);

    if (error) yield put(requestOrgChipsFailure(error));
    else yield put(requestOrgChipsSuccess(response));
}

export function* generateFetchOrgChipsWatcher() {
    yield takeLatest(FETCH_ORG_CHIPS, fetchOrgChipsSaga);
}

/* Org Overview */
function* fetchOrgOverviewSaga(action: AnyAction): SagaIterator {
    yield put(requestOrgOverview());

    const { error, response } = yield call(fetchOrgOverview, action.orgId);

    if (error) yield put(requestOrgOverviewFailure(error));
    else yield put(requestOrgOverviewSuccess(response));
}

export function* generateFetchOrgOverviewWatcher() {
    yield takeLatest(FETCH_ORG_OVERVIEW, fetchOrgOverviewSaga);
}

/* Org Yearly */
function* fetchOrgYearlySaga(action: AnyAction): SagaIterator {
    yield put(requestOrgYearly());

    const { error, response } = yield call(fetchOrgYearly, action.orgId, action.year);

    if (error) yield put(requestOrgYearlyFailure(error));
    else yield put(requestOrgYearlySuccess(response));
}

export function* generateFetchOrgYearlyWatcher() {
    yield takeLatest(FETCH_ORG_YEARLY, fetchOrgYearlySaga);
}

/* Org Transactions */
function* fetchOrgTransactionsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrgTransactions());

    const { error, response } = yield call(fetchOrgTransactions, action.orgId, action.params);

    if (error) yield put(requestOrgTransactionsFailure(error));
    else yield put(requestOrgTransactionsSuccess(response));
}

export function* generateFetchOrgTransactionsWatcher() {
    yield takeLatest(FETCH_ORG_TRANSACTIONS, fetchOrgTransactionsSaga);
}

/* Org Machines */
function* fetchOrgMachinesSaga(action: AnyAction): SagaIterator {
    yield put(requestOrgMachines());

    const { error, response } = yield call(fetchOrgMachines, action.orgId);

    if (error) yield put(requestOrgMachinesFailure(error));
    else yield put(requestOrgMachinesSuccess(response));
}

export function* generateFetchOrgMachinesWatcher() {
    yield takeLatest(FETCH_ORG_MACHINES, fetchOrgMachinesSaga);
}

/* Org Members */
function* fetchOrgMembersSaga(action: AnyAction): SagaIterator {
    yield put(requestOrgMembers());

    const { error, response } = yield call(fetchOrgMembers, action.orgId);

    if (error) yield put(requestOrgMembersFailure(error));
    else yield put(requestOrgMembersSuccess(response));
}

export function* generateFetchOrgMembersWatcher() {
    yield takeLatest(FETCH_ORG_MEMBERS, fetchOrgMembersSaga);
}

/* Org Member Mutations (machine-scoped viewer access) */
function* memberMutationError(error: any): SagaIterator {
    yield put(
        enqueueSnackbarAction({
            message: 'Fehler: Der Zugriff konnte nicht gespeichert werden! ' + (error?.message ?? ''),
            options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
    );
}

function* memberMutationSuccess(orgId: string, message: string): SagaIterator {
    yield put(
        enqueueSnackbarAction({
            message,
            options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
    );
    yield put(fetchOrgMembersAction(orgId));
}

function* addOrgMemberSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(addOrgMember, action.orgId, action.data);
    if (error) yield call(memberMutationError, error);
    else yield call(memberMutationSuccess, action.orgId, 'Zugriff wurde angelegt.');
}

export function* generateAddOrgMemberWatcher() {
    yield takeLatest(ADD_ORG_MEMBER, addOrgMemberSaga);
}

function* updateOrgMemberSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(updateOrgMember, action.orgId, action.customerId, action.data);
    if (error) yield call(memberMutationError, error);
    else yield call(memberMutationSuccess, action.orgId, 'Zugriff wurde aktualisiert.');
}

export function* generateUpdateOrgMemberWatcher() {
    yield takeLatest(UPDATE_ORG_MEMBER, updateOrgMemberSaga);
}

function* removeOrgMemberSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(removeOrgMember, action.orgId, action.customerId);
    if (error) yield call(memberMutationError, error);
    else yield call(memberMutationSuccess, action.orgId, 'Zugriff wurde entfernt.');
}

export function* generateRemoveOrgMemberWatcher() {
    yield takeLatest(REMOVE_ORG_MEMBER, removeOrgMemberSaga);
}

/* Create Machine (manual registry entry) */
function* createOrgMachineSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(createOrgMachine, action.orgId, action.data);
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Die Maschine konnte nicht angelegt werden! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: 'Maschine wurde angelegt.',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchOrgMachinesAction(action.orgId));
    }
}

export function* generateCreateOrgMachineWatcher() {
    yield takeLatest(CREATE_ORG_MACHINE, createOrgMachineSaga);
}

/* Org Transactions CSV import (admin-only) */
function* importOrgTransactionsSaga(action: AnyAction): SagaIterator {
    const { error, response } = yield call(importOrgTransactions, action.orgId, action.formData);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Der CSV-Import ist fehlgeschlagen! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        const r = response?.data ?? {};
        yield put(
            enqueueSnackbarAction({
                message:
                    `CSV-Import: ${r.imported ?? 0} importiert, ${r.duplicates ?? 0} Duplikate, ` +
                    `${r.ignored ?? 0} ignoriert, ${r.createdChips ?? 0} Chips angelegt` +
                    (r.conflicts ? `, ${r.conflicts} Konflikte` : ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        // refresh the affected org views (transactions list, chip list, KPI overview)
        yield put(fetchOrgTransactionsAction(action.orgId));
        yield put(fetchOrgChipsAction(action.orgId));
        yield put(fetchOrgOverviewAction(action.orgId));
    }
}

export function* generateImportOrgTransactionsWatcher() {
    yield takeLatest(IMPORT_ORG_TRANSACTIONS, importOrgTransactionsSaga);
}

/* Org Chips CSV import (admin-only) */
function* importOrgChipsSaga(action: AnyAction): SagaIterator {
    const { error, response } = yield call(importOrgChips, action.orgId, action.formData);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Der Chip-Import ist fehlgeschlagen! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        const r = response?.data ?? {};
        yield put(
            enqueueSnackbarAction({
                message:
                    `Chip-Import: ${r.created ?? 0} angelegt, ${r.updated ?? 0} aktualisiert` +
                    (r.conflicts ? `, ${r.conflicts} Konflikte` : '') +
                    (r.errors && r.errors.length ? `, ${r.errors.length} Fehler` : ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchOrgChipsAction(action.orgId));
        yield put(fetchOrgOverviewAction(action.orgId));
    }
}

export function* generateImportOrgChipsWatcher() {
    yield takeLatest(IMPORT_ORG_CHIPS, importOrgChipsSaga);
}

/* Chip Detail */
function* fetchChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChip());

    const { error, response } = yield call(fetchChip, action.chipId);

    if (error) yield put(requestChipFailure(error));
    else yield put(requestChipSuccess(response));
}

export function* generateFetchChipWatcher() {
    yield takeLatest(FETCH_CHIP, fetchChipSaga);
}

/* Chip Transactions */
function* fetchChipTransactionsSaga(action: AnyAction): SagaIterator {
    yield put(requestChipTransactions());

    const { error, response } = yield call(fetchChipTransactions, action.chipId, action.params);

    if (error) yield put(requestChipTransactionsFailure(error));
    else yield put(requestChipTransactionsSuccess(response));
}

export function* generateFetchChipTransactionsWatcher() {
    yield takeLatest(FETCH_CHIP_TRANSACTIONS, fetchChipTransactionsSaga);
}

/* Chip Mutations */
function* notifyChipMutationFailure(error: any): SagaIterator {
    yield put(
        enqueueSnackbarAction({
            message: 'Fehler: Die Chip-Änderung konnte nicht gespeichert werden! ' + (error?.message ?? ''),
            options: {
                key: new Date().getTime() + Math.random(),
                variant: 'error',
            },
        })
    );
    yield put(requestChipMutationFailure(error));
}

function* createChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(createChip, action.orgId, action.data);

    if (error) yield call(notifyChipMutationFailure, error);
    else {
        yield put(requestChipMutationSuccess(response));
        yield put(fetchOrgChipsAction(action.orgId));
    }
}

export function* generateCreateChipWatcher() {
    yield takeLatest(CREATE_CHIP, createChipSaga);
}

function* correctChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(correctChip, action.orgId, action.data);

    if (error) yield call(notifyChipMutationFailure, error);
    else {
        yield put(requestChipMutationSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: 'Chip wurde angelegt und dem Mitarbeiter zugeordnet.',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchOrgChipsAction(action.orgId));
        yield put(fetchOrgOverviewAction(action.orgId));
    }
}

export function* generateCorrectChipWatcher() {
    yield takeLatest(CORRECT_CHIP, correctChipSaga);
}

function* linkChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(linkChip, action.chipId, action.data);

    if (error) yield call(notifyChipMutationFailure, error);
    else {
        yield put(requestChipMutationSuccess(response));
        yield put(fetchChipAction(action.chipId));
        if (action.orgId) yield put(fetchOrgChipsAction(action.orgId));
    }
}

export function* generateLinkChipWatcher() {
    yield takeLatest(LINK_CHIP, linkChipSaga);
}

function* assignChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(assignChip, action.chipId, action.data);

    if (error) yield call(notifyChipMutationFailure, error);
    else {
        yield put(requestChipMutationSuccess(response));
        yield put(fetchChipAction(action.chipId));
        if (action.orgId) yield put(fetchOrgChipsAction(action.orgId));
    }
}

export function* generateAssignChipWatcher() {
    yield takeLatest(ASSIGN_CHIP, assignChipSaga);
}

function* fetchOrgPersonsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrgPersons());

    const { error, response } = yield call(fetchOrgPersons, action.orgId);

    if (error) yield put(requestOrgPersonsFailure(error));
    else yield put(requestOrgPersonsSuccess(response));
}

export function* generateFetchOrgPersonsWatcher() {
    yield takeLatest(FETCH_ORG_PERSONS, fetchOrgPersonsSaga);
}

function* updateChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(updateChip, action.chipId, action.data);

    if (error) yield call(notifyChipMutationFailure, error);
    else {
        yield put(requestChipMutationSuccess(response));
        yield put(fetchChipAction(action.chipId));
        if (action.orgId) yield put(fetchOrgChipsAction(action.orgId));
        else yield put(fetchMyChipsAction());
    }
}

export function* generateUpdateChipWatcher() {
    yield takeLatest(UPDATE_CHIP, updateChipSaga);
}

function* topUpChipSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(topUpChip, action.chipId, action.data);

    if (error) yield put(requestChipMutationFailure(error));
    else yield put(requestChipMutationSuccess(response));
}

export function* generateTopUpChipWatcher() {
    yield takeLatest(TOPUP_CHIP, topUpChipSaga);
}

/* Chip History */
function* fetchChipHistorySaga(action: AnyAction): SagaIterator {
    yield put(requestChipHistory());

    const { error, response } = yield call(fetchChipHistory, action.chipId);

    if (error) yield put(requestChipHistoryFailure(error));
    else yield put(requestChipHistorySuccess(response));
}

export function* generateFetchChipHistoryWatcher() {
    yield takeLatest(FETCH_CHIP_HISTORY, fetchChipHistorySaga);
}

/* Resend registration email */
function* resendChipRegistrationSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(resendChipRegistration, action.chipId);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Die Registrierungs-E-Mail konnte nicht gesendet werden! ' + (error?.message ?? ''),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: 'Registrierungs-E-Mail wurde erneut gesendet.',
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateResendChipRegistrationWatcher() {
    yield takeLatest(RESEND_CHIP_REGISTRATION, resendChipRegistrationSaga);
}

/* Request a chip type change (Umstellen) */
function* requestChipTypeChangeSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(requestChipTypeChange, action.chipId);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Die Umstellungsanfrage konnte nicht gesendet werden! ' + (error?.message ?? ''),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: 'Umstellungsanfrage wurde gesendet.',
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateRequestChipTypeChangeWatcher() {
    yield takeLatest(REQUEST_CHIP_TYPE_CHANGE, requestChipTypeChangeSaga);
}

/* Chip Yearly (per-chip monthly overview) */
function* fetchChipYearlySaga(action: AnyAction): SagaIterator {
    yield put(requestChipYearly());

    const { error, response } = yield call(fetchChipYearly, action.chipId, action.year);

    if (error) yield put(requestChipYearlyFailure(error));
    else yield put(requestChipYearlySuccess(response));
}

export function* generateFetchChipYearlyWatcher() {
    yield takeLatest(FETCH_CHIP_YEARLY, fetchChipYearlySaga);
}

/* Chip Machines */
function* fetchChipMachinesSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMachines());

    const { error, response } = yield call(fetchChipMachines, action.chipId);

    if (error) yield put(requestChipMachinesFailure(error));
    else yield put(requestChipMachinesSuccess(response));
}

export function* generateFetchChipMachinesWatcher() {
    yield takeLatest(FETCH_CHIP_MACHINES, fetchChipMachinesSaga);
}

/* My Transactions (caller-scoped) */
function* fetchMyTransactionsSaga(action: AnyAction): SagaIterator {
    yield put(requestMyTransactions());

    const { error, response } = yield call(fetchMyTransactions, action.params);

    if (error) yield put(requestMyTransactionsFailure(error));
    else yield put(requestMyTransactionsSuccess(response));
}

export function* generateFetchMyTransactionsWatcher() {
    yield takeLatest(FETCH_MY_TRANSACTIONS, fetchMyTransactionsSaga);
}

/* My Machines */
function* fetchMyMachinesSaga(): SagaIterator {
    yield put(requestMyMachines());

    const { error, response } = yield call(fetchMyMachines);

    if (error) yield put(requestMyMachinesFailure(error));
    else yield put(requestMyMachinesSuccess(response));
}

export function* generateFetchMyMachinesWatcher() {
    yield takeLatest(FETCH_MY_MACHINES, fetchMyMachinesSaga);
}

/* My Yearly (caller-scoped monthly overview) */
function* fetchMyYearlySaga(action: AnyAction): SagaIterator {
    yield put(requestMyYearly());

    const { error, response } = yield call(fetchMyYearly, action.year, action.chipId);

    if (error) yield put(requestMyYearlyFailure(error));
    else yield put(requestMyYearlySuccess(response));
}

export function* generateFetchMyYearlyWatcher() {
    yield takeLatest(FETCH_MY_YEARLY, fetchMyYearlySaga);
}

/* Set/clear reminder */
function* setChipReminderSaga(action: AnyAction): SagaIterator {
    yield put(requestChipMutation());

    const { error, response } = yield call(setChipReminder, action.chipId, action.data);

    if (error) yield call(notifyChipMutationFailure, error);
    else {
        yield put(requestChipMutationSuccess(response));
        yield put(fetchMyChipsAction());
    }
}

export function* generateSetChipReminderWatcher() {
    yield takeLatest(SET_CHIP_REMINDER, setChipReminderSaga);
}

/* Settlement Config (Abrechnung — org fees) */
function* fetchSettlementConfigSaga(action: AnyAction): SagaIterator {
    yield put(requestSettlementConfig());

    const { error, response } = yield call(fetchSettlementConfig, action.orgId);

    if (error) yield put(requestSettlementConfigFailure(error));
    else yield put(requestSettlementConfigSuccess(response));
}

export function* generateFetchSettlementConfigWatcher() {
    yield takeLatest(FETCH_SETTLEMENT_CONFIG, fetchSettlementConfigSaga);
}

function* updateSettlementConfigSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(updateSettlementConfig, action.orgId, action.data);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Die Gebühren konnten nicht gespeichert werden! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: 'Gebühren wurden gespeichert.',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchSettlementConfigAction(action.orgId));
    }
}

export function* generateUpdateSettlementConfigWatcher() {
    yield takeLatest(UPDATE_SETTLEMENT_CONFIG, updateSettlementConfigSaga);
}

/* Settlement Parties (external machine owners) */
function* fetchSettlementPartiesSaga(action: AnyAction): SagaIterator {
    yield put(requestSettlementParties());

    const { error, response } = yield call(fetchSettlementParties, action.orgId);

    if (error) yield put(requestSettlementPartiesFailure(error));
    else yield put(requestSettlementPartiesSuccess(response));
}

export function* generateFetchSettlementPartiesWatcher() {
    yield takeLatest(FETCH_SETTLEMENT_PARTIES, fetchSettlementPartiesSaga);
}

function* partyMutationError(error: any): SagaIterator {
    yield put(
        enqueueSnackbarAction({
            message: 'Fehler: Der Eigentümer konnte nicht gespeichert werden! ' + (error?.message ?? ''),
            options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
    );
}

function* partyMutationSuccess(orgId: string, message: string): SagaIterator {
    yield put(
        enqueueSnackbarAction({
            message,
            options: { key: new Date().getTime() + Math.random(), variant: 'success' },
        })
    );
    yield put(fetchSettlementPartiesAction(orgId));
}

function* createSettlementPartySaga(action: AnyAction): SagaIterator {
    const { error } = yield call(createSettlementParty, action.orgId, action.data);
    if (error) yield call(partyMutationError, error);
    else yield call(partyMutationSuccess, action.orgId, 'Eigentümer wurde angelegt.');
}

export function* generateCreateSettlementPartyWatcher() {
    yield takeLatest(CREATE_SETTLEMENT_PARTY, createSettlementPartySaga);
}

function* updateSettlementPartySaga(action: AnyAction): SagaIterator {
    const { error } = yield call(updateSettlementParty, action.orgId, action.partyId, action.data);
    if (error) yield call(partyMutationError, error);
    else yield call(partyMutationSuccess, action.orgId, 'Eigentümer wurde aktualisiert.');
}

export function* generateUpdateSettlementPartyWatcher() {
    yield takeLatest(UPDATE_SETTLEMENT_PARTY, updateSettlementPartySaga);
}

function* deleteSettlementPartySaga(action: AnyAction): SagaIterator {
    const { error } = yield call(deleteSettlementParty, action.orgId, action.partyId);
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Der Eigentümer konnte nicht gelöscht werden! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield call(partyMutationSuccess, action.orgId, 'Eigentümer wurde gelöscht.');
    }
}

export function* generateDeleteSettlementPartyWatcher() {
    yield takeLatest(DELETE_SETTLEMENT_PARTY, deleteSettlementPartySaga);
}

/* Machine Settlement (ownership / operator per machine) */
function* updateMachineSettlementSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(updateMachineSettlement, action.orgId, action.machineId, action.data);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Die Maschinen-Zuordnung konnte nicht gespeichert werden! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: 'Maschinen-Zuordnung wurde gespeichert.',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchOrgMachinesAction(action.orgId));
    }
}

export function* generateUpdateMachineSettlementWatcher() {
    yield takeLatest(UPDATE_MACHINE_SETTLEMENT, updateMachineSettlementSaga);
}

/* Product Billing (product name → article mapping) */
function* fetchProductBillingSaga(action: AnyAction): SagaIterator {
    yield put(requestProductBilling());

    const { error, response } = yield call(fetchProductBilling, action.orgId);

    if (error) yield put(requestProductBillingFailure(error));
    else yield put(requestProductBillingSuccess(response));
}

export function* generateFetchProductBillingWatcher() {
    yield takeLatest(FETCH_PRODUCT_BILLING, fetchProductBillingSaga);
}

function* replaceProductBillingSaga(action: AnyAction): SagaIterator {
    const { error } = yield call(updateProductBilling, action.orgId, action.data);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: 'Fehler: Die Produkt-Verrechnung konnte nicht gespeichert werden! ' + (error?.message ?? ''),
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(
            enqueueSnackbarAction({
                message: 'Produkt-Verrechnung wurde gespeichert.',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchProductBillingAction(action.orgId));
        yield put(fetchUnmappedProductsAction(action.orgId));
    }
}

export function* generateReplaceProductBillingWatcher() {
    yield takeLatest(REPLACE_PRODUCT_BILLING, replaceProductBillingSaga);
}

/* Unmapped Products (purchased product names without billing mapping) */
function* fetchUnmappedProductsSaga(action: AnyAction): SagaIterator {
    yield put(requestUnmappedProducts());

    const { error, response } = yield call(fetchUnmappedProducts, action.orgId);

    if (error) yield put(requestUnmappedProductsFailure(error));
    else yield put(requestUnmappedProductsSuccess(response));
}

export function* generateFetchUnmappedProductsWatcher() {
    yield takeLatest(FETCH_UNMAPPED_PRODUCTS, fetchUnmappedProductsSaga);
}
